/**
 * Icarus-Conversations repository.
 *
 * Die `icarus_conversations`-Tabelle speichert den Chat-Verlauf des
 * KI-Dokumentationsassistenten (`NutriChatSheet`). Eine Zeile = ein
 * Gespräch; `messages` ist ein JSONB-Array von `{ role, content }`.
 *
 * Vor diesem Repo lagen die drei DB-Calls (select / insert / update)
 * direkt in `NutriChatSheet.tsx` — ein Bruch der Repo-Layer-Disziplin
 * (siehe `src/data/README.md`). Dieses Modul ist die einzige Stelle,
 * die `icarus_conversations` direkt anfasst.
 */

import { supabase } from '@/integrations/supabase/client';
import { unwrap } from '@/lib/supabase-helpers';
import type { Json } from '@/integrations/supabase/types';

/**
 * Auf welchen Datensatz ein Bestätigungs-Badge zeigt. Ein Klick navigiert
 * zur zugehörigen Route (siehe `recordTargetToPath`).
 */
export type ChatRecordTarget =
  | { kind: 'patient'; patientId: string }
  | { kind: 'appointment'; appointmentId: string }
  | { kind: 'route'; to: string };

/**
 * Ein Bestätigungs-Badge für einen ausgeführten Tool-/API-Call des
 * Assistenten (z.B. ein angelegter Termin). Wird unter der jeweiligen
 * Assistant-Nachricht im Chat angezeigt.
 */
export interface ToolResultBadge {
  /** Technischer Tool-Name wie aufgerufen, z.B. "create_appointment". */
  tool: string;
  status: 'success' | 'error';
  /** Datensatz, der bei Klick geöffnet wird (falls auflösbar). */
  target?: ChatRecordTarget;
}

/** Eine einzelne Chat-Nachricht. Spiegelt das Shape im JSONB-Feld. */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /**
   * Optionale Bestätigungs-Badges für Tool-/API-Calls, die diese
   * Assistant-Nachricht ausgeführt hat. In der Conversation-JSONB
   * persistiert; auf älteren Zeilen schlicht nicht gesetzt. An die
   * Edge-Function wird immer nur das `{ role, content }`-Paar gesendet.
   */
  toolResults?: ToolResultBadge[];
}

/** Eine Conversation-Zeile, projiziert auf die Felder, die die UI braucht. */
export interface ConversationRow {
  id: string;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

/**
 * Die letzten Gespräche einer Organisation, neuste zuerst.
 * Defaultlimit 20 — die History-Sidebar zeigt nicht mehr.
 */
export async function listConversations(
  orgId: string,
  limit = 20,
): Promise<ConversationRow[]> {
  const rows = await unwrap(
    supabase
      .from('icarus_conversations')
      .select('id, messages, created_at, updated_at')
      .eq('organization_id', orgId)
      .order('updated_at', { ascending: false })
      .limit(limit),
  );
  // `messages` ist in der DB `Json`; die Spalte hält immer ein
  // ChatMessage[]-Array (von dieser App geschrieben). Cast an einer
  // Stelle statt an jeder Callsite.
  return (rows ?? []).map((r) => ({
    id: r.id,
    messages: (r.messages as unknown as ChatMessage[]) ?? [],
    created_at: r.created_at ?? '',
    updated_at: r.updated_at ?? '',
  }));
}

/**
 * Die letzten Gespräche **einer Person** innerhalb ihrer Organisation.
 *
 * `listConversations` ist bewusst org-weit (die Verlauf-Sidebar im Cockpit
 * zeigt die Gespräche des Teams). Für Auto-Restore — der globale Assistent
 * holt beim Öffnen den eigenen letzten Faden zurück — wäre das falsch: man
 * würde im Chat einer Kollegin landen. Deshalb diese user-gescopte Variante.
 */
export async function listUserConversations(
  orgId: string,
  userId: string,
  limit = 10,
): Promise<ConversationRow[]> {
  const rows = await unwrap(
    supabase
      .from('icarus_conversations')
      .select('id, messages, created_at, updated_at')
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit),
  );
  return (rows ?? []).map((r) => ({
    id: r.id,
    messages: (r.messages as unknown as ChatMessage[]) ?? [],
    created_at: r.created_at ?? '',
    updated_at: r.updated_at ?? '',
  }));
}

/**
 * Legt ein neues Gespräch an und gibt dessen id zurück, damit der
 * Aufrufer Folge-Updates auf dieselbe Zeile schreiben kann.
 */
export async function createConversation(input: {
  organizationId: string;
  userId: string;
  messages: ChatMessage[];
}): Promise<string> {
  const row = await unwrap(
    supabase
      .from('icarus_conversations')
      .insert({
        organization_id: input.organizationId,
        user_id: input.userId,
        messages: input.messages as unknown as Json,
      })
      .select('id')
      .single(),
  );
  return row.id;
}

/**
 * Aktualisiert die Nachrichten eines bestehenden Gesprächs. Der
 * `user_id`-Filter spiegelt die RLS-Policy — ein Update auf ein fremdes
 * Gespräch ist ohnehin nicht erlaubt, der Filter macht es explizit.
 */
export async function updateConversation(
  conversationId: string,
  userId: string,
  messages: ChatMessage[],
): Promise<void> {
  await unwrap(
    supabase
      .from('icarus_conversations')
      .update({
        messages: messages as unknown as Json,
        updated_at: new Date().toISOString(),
      })
      .eq('id', conversationId)
      .eq('user_id', userId)
      .select('id')
      .single(),
  );
}
